import React from "react";
import MUIButton, { ButtonProps as MUIButtonProps } from "@mui/material/Button";

/**
 * Props to use when instantiating a `Button` component.
 */
export interface ButtonProps extends MUIButtonProps {
  /**
   * Variant of the button type. Can be 'contained', 'outlined' or 'text'. Defaults to 'contained'.
   */
  variant?: "contained" | "outlined" | "text";

  /**
   * Size of the button, "small", "medium", or "large". Defaults to "medium"
   */
  size?: "small" | "medium" | "large";

  /**
   * CSS class of button, either 'inherit'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning'. Defaults to "primary".
   */
  buttonStatus?:
    | "primary"
    | "secondary"
    | "success"
    | "error"
    | "warning"
    | "info";

  /**
   * Text to display
   */
  text?: string;

  /**
   * Disabled boolean. Defaults to false.
   */
  disabled?: boolean;

  /**
   * Children to render within the button
   */
  children?: React.ReactNode;

  /**
   * link to open when the button is clicked
   */
  href?: string;

  /**
   * open link in new tab
   */
  target?: string;
}

/**
 * A button component, either a link or `button` element.
 *
 */
export function Button<C extends React.ElementType>({
  variant = "contained",
  size = "medium",
  buttonStatus = "primary",
  disabled = false,
  onClick,
  text,
  children,
  href = "",
  target,
  type = "button",
  ...buttonProps
}: MUIButtonProps<C, { component?: C }> & ButtonProps) {
  return (
    <MUIButton
      variant={variant}
      onClick={onClick || (() => {})}
      disabled={disabled}
      size={size}
      color={buttonStatus}
      href={href}
      target={target}
      rel="noreferrer"
      type={type}
      sx={{
        textTransform: "none",
        fontWeight: "bold",
      }}
      {...buttonProps}
    >
      {text}
      {children}
    </MUIButton>
  );
}
